import { Routes } from '@angular/router';
import { APP_CONSTANTS } from './core/constants/app.constants';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { ROLE_PERMISSIONS } from './core/permissions/role-permissions';
import { getStoredUserRole } from './core/utils/user-storage.utils';
import { unsavedActionItemFormGuard } from './features/settings/action-item-forms/unsaved-action-item-form.guard';
import { practiceMockSessionRoutes } from './features/practice-mock-session/practice-mock-session.routes';

export const routes: Routes = [
  {
    path: '',
    redirectTo: APP_CONSTANTS.ROUTES.LOGIN,
    pathMatch: 'full'
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(
            (m) => m.LoginComponent
          )
      },
      {
        path: 'google-callback',
        loadComponent: () =>
          import('./features/auth/google-callback/google-callback.component').then(
            (m) => m.GoogleCallbackComponent
          )
      },
      {
        path: 'microsoft-callback',
        loadComponent: () =>
          import('./features/auth/microsoft-callback/microsoft-callback.component').then(
            (m) => m.MicrosoftCallbackComponent
          )
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./features/auth/forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent
          )
      },
      {
        path: 'verify-otp',
        loadComponent: () =>
          import('./features/auth/verify-otp/verify-otp.component').then(
            (m) => m.VerifyOtpComponent
          )
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features/auth/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent
          )
      }
    ]
  },
{
  path: APP_CONSTANTS.ROUTES.DSM_DATA.replace('/', ''),
  loadComponent: () =>
    import('./features/dashboard/dsm-data/dsm-data.component').then(
      (m) => m.DsmDataComponent
    ),
  canActivate: [authGuard, roleGuard],
  data: {
    roles: [...APP_CONSTANTS.DSM_DATA_ROLES]
  }
},
{
  path: APP_CONSTANTS.ROUTES.DASHBOARD.replace('/', ''),
  loadComponent: () =>
    {
      const userRole = getStoredUserRole();

      switch (userRole) {
        case APP_CONSTANTS.ROLES.CSI:
          return import('./features/dashboard-csi/dashboard-csi.component').then(
            (m) => m.DashboardCsiComponent
          );
        case APP_CONSTANTS.ROLES.CSE:
          return import('./features/dashboard-cse/dashboard-cse.component').then(
            (m) => m.DashboardCseComponent
          );
        case APP_CONSTANTS.ROLES.TechnicalMentor:
          return import('./features/dashboard-technical-mentor/dashboard-technical-mentor.component').then(
            (m) => m.DashboardTechnicalMentorComponent
          );
        case APP_CONSTANTS.ROLES.CareerCoach:
          return import('./features/dashboard-career-coach/dashboard-career-coach.component').then(
            (m) => m.DashboardCareerCoachComponent
          );
        case APP_CONSTANTS.ROLES.CSE_TL:
          return import('./features/dashboard-cse-tl/dashboard-cse-tl.component').then(
            (m) => m.DashboardCseTlComponent
          );
        case APP_CONSTANTS.ROLES.CareerServiceSupervisor:
          return import('./features/dashboard-css/dashboard-css.component').then(
            (m) => m.DashboardCssComponent
          );
        case APP_CONSTANTS.ROLES.ADMIN:
        case APP_CONSTANTS.ROLES.MANAGER:
        default:
          return import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          );
      }
    },
  canActivate: [authGuard, roleGuard],
  data: {
    roles: ROLE_PERMISSIONS.DASHBOARD
  }
},
 {
    path: APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD.replace('/', ''),
    loadComponent: () =>
      import('./features/student-dashboard/student-dashboard.component')
        .then(m => m.StudentDashboardComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.STUDENT]
    }
  },
 
  {
    path: 'student-dashboard/applications',
    loadComponent: () =>
      import('./features/student-dashboard/student-applications/student-applications.component')
        .then(m => m.StudentApplicationsComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.STUDENT]
    }
  },

  {
    path: 'student-dashboard/interviews',
    loadComponent: () =>
      import('./features/student-dashboard/student-interviews/student-interviews.component')
        .then(m => m.StudentInterviewsComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.STUDENT]
    }
  },
  {
    path: 'student-dashboard/inbox',
    loadComponent: () =>
      import('./features/inbox/inbox-master/inbox-master.component')
        .then(m => m.InboxMasterComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.STUDENT],
      inboxView: 'inbox'
    }
  },
  {
    path: 'student-dashboard/notifications',
    loadComponent: () =>
      import('./features/inbox/inbox-master/inbox-master.component')
        .then(m => m.InboxMasterComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.STUDENT],
      inboxView: 'notifications'
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.STUDENT_ONBOARDING.replace('/', ''),
    loadComponent: () =>
      import('./features/student-onboarding/student-onboarding.component').then(
        (m) => m.StudentOnboardingComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'student-onboarding/draft-success',
    loadComponent: () =>
      import('./features/student-onboarding/draft-success/draft-success.component').then(
        (m) => m.DraftSuccessComponent
      )
  },
  {
    path: 'student-onboarding/submit-success',
    loadComponent: () =>
      import('./features/student-onboarding/submit-success/submit-success.component').then(
        (m) => m.SubmitSuccessComponent
      )
  },
  {
    path: APP_CONSTANTS.ROUTES.SETTINGS_FORMS_NEW.replace(/^\//, ''),
    loadComponent: () =>
      import('./features/settings/action-item-forms/action-item-form-editor.component').then(
        (m) => m.ActionItemFormEditorComponent
      ),
    canActivate: [authGuard, roleGuard],
    canDeactivate: [unsavedActionItemFormGuard],
    data: {
      roles: ROLE_PERMISSIONS.ACTION_ITEM_FORMS,
      create: true
    }
  },
  {
    path: `${APP_CONSTANTS.ROUTES.SETTINGS_FORMS.replace(/^\//, '')}/:id`,
    loadComponent: () =>
      import('./features/settings/action-item-forms/action-item-form-editor.component').then(
        (m) => m.ActionItemFormEditorComponent
      ),
    canActivate: [authGuard, roleGuard],
    canDeactivate: [unsavedActionItemFormGuard],
    data: {
      roles: ROLE_PERMISSIONS.ACTION_ITEM_FORMS
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.SETTINGS_FORMS.replace(/^\//, ''),
    loadComponent: () =>
      import('./features/settings/action-item-forms/action-item-form-list.component').then(
        (m) => m.ActionItemFormListComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: ROLE_PERMISSIONS.ACTION_ITEM_FORMS
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.SETTINGS_BOOKING_LIMIT.replace('/', ''),
    loadComponent: () =>
      import('./features/settings/booking-limit/booking-limit.component').then(
        (m) => m.BookingLimitComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: ROLE_PERMISSIONS.BOOKING_LIMIT
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.SETTINGS_ANNOUNCEMENT.replace('/', ''),
    loadComponent: () =>
      import('./features/settings/announcement/announcement.component').then(
        (m) => m.AnnouncementComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: ROLE_PERMISSIONS.ANNOUNCEMENTS
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.SETTINGS.replace('/', ''),
    loadComponent: () =>
      import('./features/settings/settings.component').then(
        (m) => m.SettingsComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.ADMIN, APP_CONSTANTS.ROLES.MANAGER, APP_CONSTANTS.ROLES.CareerCoach, APP_CONSTANTS.ROLES.TechnicalMentor]
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.PROFILE.replace('/', ''),
    loadComponent: () =>
      import('./features/profile/profile.component').then(
        (m) => m.ProfileComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: APP_CONSTANTS.ROUTES.STUDENTS.replace('/', ''),
    loadComponent: () =>
      import('./features/students/students-list/students-list.component').then(
        (m) => m.StudentsListComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'students/view/:id',
    loadComponent: () =>
      import('./features/students/view-student/view-student.component').then(
        (m) => m.ViewStudentComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'students/add',
    loadComponent: () =>
      import('./features/students/add-student/add-student.component').then(
        (m) => m.AddStudentComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: 'students/edit/:id',
    loadComponent: () =>
      import('./features/students/edit-student/edit-student.component').then(
        (m) => m.EditStudentComponent
      ),
     canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: 'students/manage-resumes',
    loadComponent: () =>
      import('./features/students/manage-resumes/manage-resumes.component').then(
        (m) => m.ManageResumesComponent
      ),
     canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CSI,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
 {
  path: APP_CONSTANTS.ROUTES.INTERVIEWS.replace('/', ''),
  loadComponent: () =>
    import('./features/interviews/interviews-list/interviews-list.component')
      .then((m) => m.InterviewsListComponent),
  canActivate: [authGuard, roleGuard],
  data: {
    roles: [
      APP_CONSTANTS.ROLES.ADMIN,
      APP_CONSTANTS.ROLES.MANAGER,
      APP_CONSTANTS.ROLES.CSE_TL,
      APP_CONSTANTS.ROLES.CareerServiceSupervisor,
      APP_CONSTANTS.ROLES.CSE,
      APP_CONSTANTS.ROLES.CSI,
      APP_CONSTANTS.ROLES.CareerCoach,
      APP_CONSTANTS.ROLES.TechnicalMentor
    ]
  }
},
{
  path: 'interviews/add',
  loadComponent: () =>
    import('./features/interviews/add-interview/add-interview.component')
      .then((m) => m.AddInterviewComponent),
  canActivate: [authGuard, roleGuard],
  data: {
    roles: [
      APP_CONSTANTS.ROLES.ADMIN,
      APP_CONSTANTS.ROLES.MANAGER,
      APP_CONSTANTS.ROLES.CSE_TL,
      APP_CONSTANTS.ROLES.CareerServiceSupervisor,
      APP_CONSTANTS.ROLES.CSE,
      APP_CONSTANTS.ROLES.CareerCoach,
      APP_CONSTANTS.ROLES.TechnicalMentor
    ]
  }
},
  
{
  path: 'interviews/edit/:id',
  loadComponent: () =>
    import('./features/interviews/edit-interview/edit-interview.component')
      .then((m) => m.EditInterviewComponent),
  canActivate: [authGuard, roleGuard],
  data: {
    roles: [
      APP_CONSTANTS.ROLES.ADMIN,
      APP_CONSTANTS.ROLES.MANAGER,
      APP_CONSTANTS.ROLES.CSE_TL,
      APP_CONSTANTS.ROLES.CareerServiceSupervisor,
      APP_CONSTANTS.ROLES.CSE,
      APP_CONSTANTS.ROLES.CareerCoach,
      APP_CONSTANTS.ROLES.TechnicalMentor
    ]
  }
},

 {
    path: APP_CONSTANTS.ROUTES.JOB.replace('/', ''),
    loadComponent: () =>
      import('./features/jobs/jobs-list/jobs-list.component')
        .then(m => m.JobsListComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CSI,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: 'job/add',
    loadComponent: () =>
      import('./features/jobs/add-job/add-job.component').then(
        (m) => m.AddJobComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: APP_CONSTANTS.ROUTES.ADD_SCRAP_JOB.replace('/', ''),
    loadComponent: () =>
      import('./features/jobs/add-scrap-job/add-scrap-job.component').then(
        (m) => m.AddScrapJobComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'job/edit/:id',
    loadComponent: () =>
      import('./features/jobs/edit-job/edit-job.component').then(
        (m) => m.EditJobComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'job/apply/:id',
    loadComponent: () =>
      import('./features/jobs/apply-job/apply-job.component').then(
        (m) => m.ApplyJobComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'company',
    children: [
      {
        path: 'company-merge',
        loadComponent: () =>
          import('./features/settings/company-merge/company-merge.component').then(
            (m) => m.CompanyMergeComponent
          ),
        canActivate: [authGuard, roleGuard],
        data: {
          roles: ROLE_PERMISSIONS.COMPANY_MERGE
        }
      },
      {
        path: 'add',
        loadComponent: () =>
          import('./features/company/add-company/add-company.component').then(
            (m) => m.AddCompanyComponent
          ),
        canActivate: [authGuard, roleGuard]
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import('./features/company/edit-company/edit-company.component').then(
            (m) => m.EditCompanyComponent
          ),
        canActivate: [authGuard, roleGuard]
      },
      {
        path: '',
        loadComponent: () =>
          import('./features/company/company-list/company-list.component').then(
            (m) => m.CompanyListComponent
          ),
        canActivate: [authGuard, roleGuard],
        data: {
          roles: [
            APP_CONSTANTS.ROLES.ADMIN,
            APP_CONSTANTS.ROLES.MANAGER,
            APP_CONSTANTS.ROLES.CSE_TL,
            APP_CONSTANTS.ROLES.CareerServiceSupervisor,
            APP_CONSTANTS.ROLES.CSE,
            APP_CONSTANTS.ROLES.CSI,
            APP_CONSTANTS.ROLES.CareerCoach,
            APP_CONSTANTS.ROLES.TechnicalMentor
          ]
        }
      }
    ]
  },
   {
    path: APP_CONSTANTS.ROUTES.APPLICATIONS.replace('/', ''),
    loadComponent: () =>
      import('./features/applications/applications-list/applications-list.component')
        .then(m => m.ApplicationsListComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CSI,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: 'applications/add',
    loadComponent: () =>
      import('./features/applications/add-application/add-application.component').then(
        (m) => m.AddApplicationComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'applications/edit/:id',
    loadComponent: () =>
      import('./features/applications/edit-application/edit-application.component').then(
        (m) => m.EditApplicationComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'applications/easy-application',
    loadComponent: () =>
      import('./features/applications/easy-application/easy-application.component').then(
        (m) => m.EasyApplicationComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
         APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CSI,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.QUICK_APPLICATION.replace('/', ''),
    loadComponent: () =>
      import('./features/quick-application/quick-application.component').then(
        (m) => m.QuickApplicationComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CSE,
        APP_CONSTANTS.ROLES.CSI,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.USERS.replace('/', ''),
    loadComponent: () =>
      import('./features/users/users-list/users-list.component').then(
        (m) => m.UsersListComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'users/add',
    loadComponent: () =>
      import('./features/users/add-user/add-user.component').then(
        (m) => m.AddUserComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'users/edit/:id',
    loadComponent: () =>
      import('./features/users/edit-user/edit-user.component').then(
        (m) => m.EditUserComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: APP_CONSTANTS.ROUTES.REPORTS.replace('/', ''),
    loadChildren: () =>
      import('./features/reports/reports.routes').then(
        (m) => m.REPORTS_ROUTES
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      // CareerServiceSupervisor only has access to the CSI/CSA KPI report child
      // (enforced by roleGuard via the URL-specific rule); listed here for
      // visibility.
      roles: [
        APP_CONSTANTS.ROLES.ADMIN,
        APP_CONSTANTS.ROLES.MANAGER,
        APP_CONSTANTS.ROLES.CSE_TL,
        APP_CONSTANTS.ROLES.CareerServiceSupervisor,
        APP_CONSTANTS.ROLES.CareerCoach,
        APP_CONSTANTS.ROLES.TechnicalMentor
      ]
    }
  },
  // {
  //   path: APP_CONSTANTS.ROUTES.AUDIT_LOGS.replace('/', ''),
  //   loadComponent: () =>
  //     import('./features/audit-logs/audit-logs-list/audit-logs-list.component').then(
  //       (m) => m.AuditLogsListComponent
  //     ),
  //   canActivate: [authGuard]
  // },
  {
    path: APP_CONSTANTS.ROUTES.PRIVACY_POLICY.replace('/', ''),
    loadComponent: () =>
      import('./features/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      )
  },
  {
    path: APP_CONSTANTS.ROUTES.CALENDAR.replace('/', ''),
    loadComponent: () =>
      import('./features/calendar/admin-calendar-page/admin-calendar-page.component').then(
        (m) => m.AdminCalendarPageComponent
      ),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [APP_CONSTANTS.ROLES.CC, APP_CONSTANTS.ROLES.TM,APP_CONSTANTS.ROLES.STUDENT]
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.CSA_LEADERBOARD.replace('/', ''),
    loadComponent: () =>
      import('./features/leaderboard/csa-leaderboard/csa-leaderboard.component').then(
        (m) => m.CsaLeaderboardComponent
      )
  },
  {
    path: APP_CONSTANTS.ROUTES.CSE_LEADERBOARD.replace('/', ''),
    loadComponent: () =>
      import('./features/leaderboard/cse-leaderboard/cse-leaderboard.component').then(
        (m) => m.CseLeaderboardComponent
      )
  },
  {
    path: APP_CONSTANTS.ROUTES.LEADERBOARD.replace('/', ''),
    loadComponent: () =>
      import('./features/leaderboard/leaderboard-display/leaderboard-display.component').then(
        (m) => m.LeaderboardDisplayComponent
      )
  },
  {
    path: APP_CONSTANTS.ROUTES.INBOX.replace('/', ''),
    loadComponent: () =>
      import('./features/inbox/inbox-master/inbox-master.component').then(m => m.InboxMasterComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: Object.values(APP_CONSTANTS.ROLES),
      inboxView: 'inbox'
    }
  },
  {
    path: APP_CONSTANTS.ROUTES.NOTIFICATIONS.replace('/', ''),
    loadComponent: () =>
      import('./features/inbox/inbox-master/inbox-master.component').then(m => m.InboxMasterComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: Object.values(APP_CONSTANTS.ROLES),
      inboxView: 'notifications'
    }
  },
  ...practiceMockSessionRoutes,
  {
    path: '**',
    redirectTo: APP_CONSTANTS.ROUTES.LOGIN
  }
];
